import { useCallback, useMemo } from 'react';
import { useInstance } from '@milkdown/react';
import { linkSchema, toggleLinkCommand, updateLinkCommand, } from '@milkdown/preset-commonmark';
import { createClickFunction } from '../../util/notes';
import { editorViewCtx } from '@milkdown/core';
import { useNotesToolbarContext } from '../../context/notes/notes_toolbar_context';
import { useEditorStateContext } from '../../context/notes/notes_editor_state_context';
import { CREATE_LINK_SUFFIX, UPDATE_LINK_URL_SUFFIX, DELETE_LINK_URL_SUFFIX, } from '../../components/notes/consts';
export function useCreateLinkModal() {
    const [isLoading, getEditorInstance] = useInstance();
    const { captureButtonClick, inCallOrPostCall } = useEditorStateContext();
    const editor = getEditorInstance();
    const { linkContext } = useNotesToolbarContext();
    const { modalState, selectionRange, isExistingLinkYn, setModalState, isEmptySelection } = linkContext;
    const handleRemoveLink = useCallback((range) => {
        if (!isLoading) {
            editor.action(ctx => {
                const editorView = ctx.get(editorViewCtx);
                const { state } = editorView;
                const { tr } = state;
                const linkMarkType = linkSchema.type(editor.ctx);
                editorView.dispatch(tr.removeMark(range.from, range.to, linkMarkType));
            });
            typeof captureButtonClick === 'function' &&
                captureButtonClick(`${inCallOrPostCall}-${DELETE_LINK_URL_SUFFIX}`);
        }
    }, [captureButtonClick, editor, inCallOrPostCall, isLoading]);
    const handleCreateLink = useCallback((payload) => {
        if (!isLoading) {
            const linkClickFunction = createClickFunction(getEditorInstance, isLoading, toggleLinkCommand.key, payload);
            linkClickFunction();
            typeof captureButtonClick === 'function' &&
                captureButtonClick(`${inCallOrPostCall}-${CREATE_LINK_SUFFIX}`);
        }
    }, [captureButtonClick, getEditorInstance, inCallOrPostCall, isLoading]);
    const handleReplaceTextAndCreateLink = useCallback((text, range, payload) => {
        if (!isLoading) {
            editor.action(ctx => {
                const editorView = ctx.get(editorViewCtx);
                const { state } = editorView;
                const linkMark = linkSchema.type(editor.ctx).create(payload);
                // `from` can be undefined when there is no text in an editor, falling back to 0 allows creation of a link.
                // `to` doesn't need a fallback, `replaceWith` handles undefined internally
                const transaction = state.tr.replaceWith(range?.from ?? 0, range?.to, state.schema.text(text, [linkMark]));
                editorView.dispatch(transaction);
            });
            typeof captureButtonClick === 'function' &&
                captureButtonClick(`${inCallOrPostCall}-${CREATE_LINK_SUFFIX}`);
        }
    }, [captureButtonClick, editor, inCallOrPostCall, isLoading]);
    const handleEditLink = useCallback((payload) => {
        if (!isLoading) {
            const editLinkClickFunction = createClickFunction(getEditorInstance, isLoading, updateLinkCommand.key, payload);
            editLinkClickFunction();
            typeof captureButtonClick === 'function' &&
                captureButtonClick(`${inCallOrPostCall}-${UPDATE_LINK_URL_SUFFIX}`);
        }
    }, [captureButtonClick, getEditorInstance, inCallOrPostCall, isLoading]);
    const handleLinkActions = useCallback(() => {
        const titleChanged = modalState.title !== modalState.initialTitle;
        const linkChanged = modalState.initialLink !== modalState.href;
        const linkRemoved = linkChanged && modalState.href === '';
        const linkData = {
            href: modalState.href,
        };
        const titleText = modalState.title === '' ? modalState.href : modalState.title;
        if (isExistingLinkYn && linkRemoved) {
            handleRemoveLink(selectionRange);
        }
        else if (isExistingLinkYn && linkChanged && !titleChanged) {
            handleEditLink(linkData);
        }
        else if (modalState.href !== '' && !isExistingLinkYn && !isEmptySelection) {
            handleCreateLink(linkData);
        }
        else {
            handleReplaceTextAndCreateLink(titleText, selectionRange, linkData);
        }
    }, [
        modalState.title,
        modalState.initialTitle,
        modalState.initialLink,
        modalState.href,
        isExistingLinkYn,
        isEmptySelection,
        handleRemoveLink,
        selectionRange,
        handleEditLink,
        handleCreateLink,
        handleReplaceTextAndCreateLink,
    ]);
    const handleClose = useCallback(() => {
        setModalState({ ...modalState, open: false });
    }, [modalState, setModalState]);
    const handleValueChange = useCallback((changeEvent) => {
        setModalState(state => ({
            ...state,
            [changeEvent.target.name]: changeEvent.target.value,
        }));
    }, [setModalState]);
    const handleCreateAndClose = useCallback(() => {
        handleLinkActions();
        handleClose();
    }, [handleClose, handleLinkActions]);
    const getLinkButtonText = useCallback(() => {
        if (!isExistingLinkYn) {
            return 'Insert';
        }
        if (modalState.href === '') {
            return 'Delete';
        }
        if (modalState.href !== modalState.initialLink ||
            modalState.title !== modalState.initialTitle)
            return 'Update';
        return 'Update';
    }, [
        isExistingLinkYn,
        modalState.href,
        modalState.initialLink,
        modalState.title,
        modalState.initialTitle,
    ]);
    return useMemo(() => {
        return {
            handleEditLink,
            handleReplaceTextAndCreateLink,
            handleLinkActions,
            handleClose,
            handleValueChange,
            handleCreateAndClose,
            getLinkButtonText,
        };
    }, [
        handleEditLink,
        handleLinkActions,
        handleReplaceTextAndCreateLink,
        handleClose,
        handleValueChange,
        handleCreateAndClose,
        getLinkButtonText,
    ]);
}
//# sourceMappingURL=use_create_link_modal.js.map